import { useId, useState } from 'react';
import type { ChangeEvent, DragEvent, ReactElement } from 'react';
import { UploadCloud } from 'lucide-react';
import { cn } from '@xdocs/ui';
import { uploadMimeType } from '@xdocs/schemas';
import { useUploadsT } from './useUploadsT.js';
import styles from './UploadDropzone.module.css';

export interface UploadDropzoneProps {
  onFilesSelected: (files: FileList | readonly File[]) => void;
  className?: string | undefined;
}

const ACCEPT = '.pdf,.doc,.docx,.txt,' + uploadMimeType.options.join(',');

/**
 * Drag-drop + native file-picker source (F18). Pure UI/selection surface —
 * every file handed to `onFilesSelected` is still client-side
 * pre-validated by `useUploadQueue.addFiles` (E6, mirrors the server's
 * `validateUploadConstraints`); the `accept` attribute here is a UX
 * convenience filter only, never the trust boundary.
 */
export function UploadDropzone({ onFilesSelected, className }: UploadDropzoneProps): ReactElement {
  const { t } = useUploadsT();
  const inputId = useId();
  const [isDragOver, setIsDragOver] = useState(false);

  function handleDrop(event: DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    setIsDragOver(false);
    if (event.dataTransfer.files.length > 0) onFilesSelected(event.dataTransfer.files);
  }

  function handleDragOver(event: DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    setIsDragOver(true);
  }

  function handleDragLeave(): void {
    setIsDragOver(false);
  }

  function handleInputChange(event: ChangeEvent<HTMLInputElement>): void {
    if (event.target.files && event.target.files.length > 0) onFilesSelected(event.target.files);
    event.target.value = '';
  }

  return (
    <div
      className={cn(styles['dropzone'], isDragOver && styles['dragOver'], className)}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      <UploadCloud size={34} className={styles['icon']} aria-hidden="true" />
      <label htmlFor={inputId} className={styles['label']}>
        {t('uploads:dropzone.cta')}
      </label>
      <p className={styles['hint']}>{t('uploads:dropzone.hint')}</p>
      <input
        id={inputId}
        type="file"
        multiple
        accept={ACCEPT}
        className={styles['input']}
        onChange={handleInputChange}
        aria-label={t('uploads:dropzone.browse')}
      />
    </div>
  );
}
